import { colors } from "goi_common";

/**
 * 장례식장 비용 안내 리드 퍼널 전용 인라인 SVG.
 *
 * `ColorIcon`(constants/icon.tsx) 에 대응하는 형태가 없는 것만 여기 둔다.
 * 첫 스텝의 빈소/음식/제단꽃 아이콘은 라이브러리(`funeral-hall` 등)를 쓴다.
 */

/** 다이어그램 전용 색.
 *
 * 팔레트에 "낭비/경고"를 뜻하는 테라코타 계열이 없다. `danger`(#E2452B)는 채도가 높아
 * 브랜드 오렌지 옆에 놓으면 경고가 아니라 두 번째 강조로 읽힌다. 이 두 색은 장식이 아니라
 * "적정(파랑) vs 낭비(빨강)"를 구분하는 **데이터 인코딩**이라 디자인 값을 유지한다.
 * 파랑은 팔레트의 blue100 / blue400 / blue700 이 디자인 값과 거의 같아 토큰을 쓴다.
 */
export const COST_FUNNEL_WASTE_COLOR = {
  fill: "#FBE2DA",
  stroke: "#D45A38",
  text: "#C33C1C",
} as const;

/**
 * 빈소 및 시설 이용료.
 *
 * 라이브러리의 `funeral-hall` 은 솔리드 채움이라 이 카드에서 너무 무겁게 읽혀 디자인의
 * 라인 아이콘을 쓴다. 음식·제단꽃은 라이브러리 아이콘(`funeral_restaurant`,
 * `mingcute_sunflower_line`)이다.
 */
export function BuildingIcon({ color = colors.orange650 }: { color?: string }) {
  return (
    <svg
      width="21"
      height="21"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 21V6a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v15" />
      <path d="M14 10h5a1 1 0 0 1 1 1v10" />
      <path d="M2 21h20" />
      <path d="M7 9h4" />
      <path d="M7 13h4" />
      <path d="M7 17h4" />
    </svg>
  );
}

/** 빈소 안 조문객 */
export function PersonIcon({ color = colors.blue700 }: { color?: string }) {
  return (
    <svg
      width="20"
      height="22"
      viewBox="0 0 24 26"
      fill="none"
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx="12" cy="7" r="4.4" />
      <path d="M4.5 24v-3.4A4.6 4.6 0 0 1 9.1 16h5.8a4.6 4.6 0 0 1 4.6 4.6V24" />
    </svg>
  );
}

/** 원형 느낌표 */
export function AlertCircleIcon({
  color = COST_FUNNEL_WASTE_COLOR.stroke,
  size = 24,
}: {
  color?: string;
  size?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9.4" strokeWidth={1.8} />
      <path d="M12 7.2v6" strokeWidth={2.8} />
      <path d="M12 16.6h.01" strokeWidth={2.8} />
    </svg>
  );
}

/** 카카오 말풍선. CTA 라벨 왼쪽. */
export function KakaoBubbleIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 3C6.9 3 2.8 6.2 2.8 10.2c0 2.5 1.7 4.7 4.2 6l-1 3.6c-.1.3.2.5.5.4l4.3-2.8c.4 0 .8.1 1.2.1 5.1 0 9.2-3.2 9.2-7.3S17.1 3 12 3z" />
    </svg>
  );
}

/**
 * "남는 공간" 오목 육각형 점선 + 연결 화살표.
 *
 * 점선의 오른쪽 변(x=200)에서 화살표가 정확히 시작해야 해서 **하나의 SVG 좌표계**를 공유한다.
 * 화살표는 박스 바깥(x>208)으로 나가므로 `overflow: visible` 이 필수다.
 * `preserveAspectRatio="none"` 으로 박스 폭에 맞춰 늘어나되, stroke 는
 * `vector-effect="non-scaling-stroke"` 로 두께와 점선 간격이 왜곡되지 않게 한다.
 */
export function VenueWasteOutline() {
  const { fill, stroke } = COST_FUNNEL_WASTE_COLOR;
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 208 138"
      preserveAspectRatio="none"
      style={{ position: "absolute", inset: 0, display: "block", overflow: "visible" }}
      aria-hidden
    >
      <path
        d="M200 69 H232"
        fill="none"
        stroke={stroke}
        strokeWidth={1.8}
        vectorEffect="non-scaling-stroke"
      />
      <path
        d="M226 64 L232.5 69 L226 74"
        fill="none"
        stroke={stroke}
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d="M103 8 H193 Q200 8 200 15 V123 Q200 130 193 130 H15 Q8 130 8 123 V51 Q8 44 15 44 H89 Q96 44 96 37 V15 Q96 8 103 8 Z"
        fill={fill}
        stroke={stroke}
        strokeWidth={1.6}
        strokeDasharray="6 5"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
